// frontend/src/components/OverallSummary.js

import React from 'react';
import {
    Box,
    Typography,
    Paper,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import moment from 'moment';
import CustomTable from './CustomTable'; // Import the CustomTable component

const OverallSummary = ({ summaryData, granularity }) => {
    const monthlyDispatchChartData = React.useMemo(() => {
        const grouped = new Map();

        summaryData.forEach((row) => {
            let periodMoment = null;
            if (granularity === 'Day') {
                periodMoment = moment(row.date);
            } else if (granularity === 'Week') {
                periodMoment = moment(row.weekStartDate);
            } else {
                const year = Number(row.year ?? row.Year);
                const month = Number(row.month);
                if (Number.isFinite(year) && Number.isFinite(month)) {
                    periodMoment = moment({ year, month: month - 1, day: 1 });
                }
            }

            if (!periodMoment || !periodMoment.isValid()) return;

            const monthKey = periodMoment.format('YYYY-MM');
            const existing = grouped.get(monthKey) || {
                monthKey,
                label: periodMoment.format('MMM YYYY'),
                ReceivedOrders: 0,
                DispatchedOnDemand: 0,
            };

            existing.ReceivedOrders += Number(row.receivedOrders || 0);
            existing.DispatchedOnDemand += Number(row.dispatchedOrders || 0);
            grouped.set(monthKey, existing);
        });

        return Array.from(grouped.values())
            .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
            .map((item) => ({
                ...item,
                UndispatchedFromReceived: Math.max(item.ReceivedOrders - item.DispatchedOnDemand, 0),
                DispatchRatePct: item.ReceivedOrders > 0
                    ? Number(((item.DispatchedOnDemand * 100) / item.ReceivedOrders).toFixed(1))
                    : 100,
            }));
    }, [summaryData, granularity]);

    // Define table columns
    const columns = [
        {
            id: 'Period',
            label: 'Period',
            minWidth: 150,
            align: 'left',
            sortable: true,
        },
        {
            id: 'ReceivedOrders',
            label: 'Received Orders',
            minWidth: 150,
            align: 'right',
            sortable: true,
        },
        {
            id: 'DispatchedOrders',
            label: 'Dispatched Orders',
            minWidth: 150,
            align: 'right',
            sortable: true,
        },
        {
            id: 'CancelledOrders',
            label: 'Cancelled Orders',
            minWidth: 150,
            align: 'right',
            sortable: true,
        },
    ];

    // State for pagination
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const renderDispatchedTopLabel = (props) => {
        const {
            x, y, width, value, payload,
        } = props;

        if (typeof x !== 'number' || typeof y !== 'number' || typeof width !== 'number') return null;

        const dispatchCount = Number(value || 0);
        const dispatchRatePct = Number(payload?.DispatchRatePct || 0).toFixed(2);

        return (
            <text
                x={x + (width / 2)}
                y={y - 6}
                fill="#1f2937"
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
            >
                {`${dispatchCount.toLocaleString()} (${dispatchRatePct}%)`}
            </text>
        );
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                    Overall Order Performance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Management view of order intake and dispatch completion across the selected period.
                </Typography>
            </Box>
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Orders Received vs Dispatched on Demand (Monthly)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Monthly stacked view for the selected date range.
                </Typography>
                {monthlyDispatchChartData.length > 0 ? (
                    <Box sx={{ width: '100%', height: { xs: 320, md: 410 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={monthlyDispatchChartData}
                                margin={{
                                    top: 24, right: 20, left: 0, bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    formatter={(value, name, entry) => {
                                        const numericValue = Number(value || 0);
                                        const dispatchRatePct = Number(entry?.payload?.DispatchRatePct || 0);

                                        if (name === 'Dispatched on Demand') {
                                            return [`${numericValue.toLocaleString()} (${dispatchRatePct.toFixed(2)}%)`, name];
                                        }

                                        if (name === 'Not Yet Dispatched') {
                                            const notYetPct = Math.max(100 - dispatchRatePct, 0);
                                            return [`${numericValue.toLocaleString()} (${notYetPct.toFixed(2)}%)`, name];
                                        }

                                        return [numericValue.toLocaleString(), name];
                                    }}
                                />
                                <Legend />
                                <Bar
                                    dataKey="DispatchedOnDemand"
                                    stackId="received"
                                    fill="#16856a"
                                    name="Dispatched on Demand"
                                    label={renderDispatchedTopLabel}
                                />
                                <Bar dataKey="UndispatchedFromReceived" stackId="received" fill="#1565c0" name="Not Yet Dispatched" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                ) : (
                    <Typography variant="body1" color="textSecondary">
                        No data available to display the chart.
                    </Typography>
                )}
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, overflow: 'hidden' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
                    Overall Summary
                </Typography>
                {summaryData.length > 0 ? (
                    <CustomTable
                        columns={columns}
                        data={summaryData.map(row => ({
                            Period: granularity === 'Day'
                                ? moment(row.date).format('YYYY-MM-DD')
                                : granularity === 'Week'
                                    ? `Week Starting ${moment(row.weekStartDate).format('YYYY-MM-DD')}`
                                    : moment({ year: Number(row.year ?? row.Year), month: Number(row.month) - 1 }).format('YYYY MMMM'),
                            ReceivedOrders: row.receivedOrders,
                            DispatchedOrders: row.dispatchedOrders,
                            CancelledOrders: row.cancelledOrders,
                        }))}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                ) : (
                    <Typography variant="body1" color="textSecondary">
                        No summary data available for the selected criteria.
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default OverallSummary;
